import {
  SettingsUser,
  UserProfile,
  Connection,
  EmailPreferences,
  KindleSettings,
  ProfileUpdateData,
  ImportResult,
  ActionStatusResult,
} from '@/types/settings';
import { dataHeaders } from './apiClient';
import { clearClientSession } from '@/utils/logout';
import { getSuccessUrl } from '@/utils/successUrl';

interface SettingsResponse {
  user: SettingsUser;
  profile: UserProfile;
  connections: Connection[];
  emailPreferences: EmailPreferences;
  kindleSettings: KindleSettings;
}

// Mock data for development
const mockSettingsData: SettingsResponse = {
  user: {
    id: '123',
    email: 'user@example.com',
    username: 'testuser',
    isSubscriber: true,
    formKey: 'test-form-key',
    rssKey: 'mock-rss-key',
    tags: [],
    folders: [],
    counts: { unread: 0, liked: 0, archived: 0, notes: 0 },
    subscription: { type: 'premium', expiresAt: '2024-12-31' },
  },
  profile: {
    fullName: 'Test User',
    description: 'Avid reader and tech enthusiast',
    avatarUrl: 'https://via.placeholder.com/150',
    website: 'https://example.com',
    location: 'San Francisco, CA',
  },
  connections: [
    { service: 'twitter', connected: false },
    { service: 'pinboard', connected: false },
    { service: 'evernote', connected: false },
    { service: 'notion', connected: false },
    { service: 'tumblr', connected: false },
  ],
  emailPreferences: {
    instapaperWeekly: true,
    productUpdates: true,
    accountUpdates: true,
  },
  kindleSettings: {
    email: 'user@kindle.com',
    verified: true,
  },
};

export const settingsService = {
  getSettings: async (): Promise<SettingsResponse> => {
    const response = await fetch('/data/user/settings');
    if (!response.ok) throw new Error('Failed to fetch settings');
    const data = await response.json();

    // Transform snake_case to camelCase for user fields
    if (data.user) {
      if (data.user.form_key) {
        data.user.formKey = data.user.form_key;
      }
      data.user.validStudentDiscount = data.user.valid_student_discount ?? false;
    }

    return data;
  },

  // Account
  updateEmail: async (email: string, formKey: string): Promise<string> => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('form_key', formKey);
    // Add success_url to redirect back to React app after save
    formData.append('success_url', getSuccessUrl('/settings/account'));

    const response = await fetch('/user/username', {
      method: 'POST',
      body: formData,
      credentials: 'include',
      redirect: 'follow',
    });

    if (response.redirected && response.url) {
      const redirectUrl = new URL(response.url);
      if (redirectUrl.origin !== window.location.origin) {
        window.location.href = response.url;
        throw new Error('Redirected to external URL');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || 'Failed to update email');
    }

    return email;
  },

  updatePassword: async (
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
    formKey: string,
  ): Promise<void> => {
    const formData = new FormData();
    formData.append('current_password', currentPassword);
    formData.append('password', newPassword);
    formData.append('password2', confirmPassword);
    formData.append('form_key', formKey);
    // Add success_url to redirect back to React app after save
    formData.append('success_url', getSuccessUrl('/settings/account'));

    const response = await fetch('/user/password', {
      method: 'POST',
      body: formData,
      credentials: 'include',
      redirect: 'follow',
    });

    if (response.redirected && response.url) {
      const redirectUrl = new URL(response.url);
      if (redirectUrl.origin !== window.location.origin) {
        window.location.href = response.url;
        throw new Error('Redirected to external URL');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || errorData.message || 'Failed to update password',
      );
    }
  },

  updateEmailPreferences: async (
    preferences: EmailPreferences,
    formKey: string,
  ): Promise<EmailPreferences> => {
    const formData = new FormData();
    formData.append('form_key', formKey);
    formData.append('daily_weekly', preferences.instapaperWeekly ? '1' : '0');
    formData.append('marketing', preferences.productUpdates ? '1' : '0');
    formData.append('account', preferences.accountUpdates ? '1' : '0');
    // Add success_url to redirect back to React app after save
    formData.append('success_url', getSuccessUrl('/settings/email'));

    const response = await fetch('/user/email_settings', {
      method: 'POST',
      body: formData,
      credentials: 'include',
      redirect: 'follow',
    });

    if (response.redirected && response.url) {
      const redirectUrl = new URL(response.url);
      if (redirectUrl.origin !== window.location.origin) {
        window.location.href = response.url;
        throw new Error('Redirected to external URL');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || errorData.message || 'Failed to update email preferences',
      );
    }

    return preferences;
  },

  // Profile
  updateDisplayName: async (displayName: string, formKey: string): Promise<string> => {
    const formData = new FormData();
    formData.append('username', displayName);
    formData.append('form_key', formKey);
    // Add success_url to redirect back to React app after save
    formData.append('success_url', getSuccessUrl('/settings/profile'));

    const response = await fetch('/user/displayname', {
      method: 'POST',
      body: formData,
      credentials: 'include',
      redirect: 'follow',
    });

    if (response.redirected && response.url) {
      const redirectUrl = new URL(response.url);
      if (redirectUrl.origin !== window.location.origin) {
        window.location.href = response.url;
        throw new Error('Redirected to external URL');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || errorData.message || 'Failed to update display name',
      );
    }

    return displayName;
  },

  updateUsername: async (username: string, formKey: string): Promise<string> => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('form_key', formKey);
    // Add success_url to redirect back to React app after save
    formData.append('success_url', getSuccessUrl('/settings/profile'));

    const response = await fetch('/user/vanity_url', {
      method: 'POST',
      body: formData,
      credentials: 'include',
      redirect: 'follow',
    });

    if (response.redirected && response.url) {
      const redirectUrl = new URL(response.url);
      if (redirectUrl.origin !== window.location.origin) {
        window.location.href = response.url;
        throw new Error('Redirected to external URL');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || errorData.message || 'Failed to update username',
      );
    }

    return username;
  },

  togglePrivacy: async (formKey: string): Promise<boolean> => {
    const params = new URLSearchParams({
      form_key: formKey,
      // Add success_url to redirect back to React app after save
      success_url: getSuccessUrl('/settings/profile'),
    });

    const response = await fetch(`/user/privacy_toggle?${params.toString()}`, {
      method: 'GET',
      credentials: 'include',
      redirect: 'follow',
    });

    if (response.redirected && response.url) {
      const redirectUrl = new URL(response.url);
      if (redirectUrl.origin !== window.location.origin) {
        window.location.href = response.url;
        throw new Error('Redirected to external URL');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || 'Failed to toggle privacy');
    }

    // Return the new privacy state (we'll need to determine this from the response or current state)
    // For now, we'll return a placeholder - the caller should track the state
    return true;
  },

  updateProfile: async (data: ProfileUpdateData): Promise<UserProfile> => {
    // Stub for development
    if (import.meta.env.DEV) {
      return Promise.resolve({ ...mockSettingsData.profile, ...data });
    }

    const response = await fetch('/user/profile', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update profile');
    return response.json();
  },

  uploadAvatar: async (file: File, formKey: string): Promise<{ avatarUrl: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('form_key', formKey);

    const response = await fetch('/data/user/upload_profile_image', {
      method: 'POST',
      // Multipart is a "simple request", so the CSRF gate on this endpoint is
      // what stops a cross-site <form> from replacing the user's avatar.
      headers: dataHeaders({}, formKey),
      credentials: 'same-origin',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to upload avatar');
    }

    const data = await response.json();
    return { avatarUrl: data.avatarUrl || '' };
  },

  // Connections
  connectService: async (service: string): Promise<{ authUrl: string }> => {
    // Stub for development
    if (import.meta.env.DEV) {
      return Promise.resolve({ authUrl: `https://example.com/oauth/${service}` });
    }

    const response = await fetch(`/user/connect/${service}`, { method: 'POST' });
    if (!response.ok) throw new Error('Failed to connect service');
    return response.json();
  },

  disconnectService: async (service: string): Promise<void> => {
    // Stub for development
    if (import.meta.env.DEV) {
      return Promise.resolve();
    }

    const response = await fetch(`/user/disconnect/${service}`, { method: 'POST' });
    if (!response.ok) throw new Error('Failed to disconnect service');
  },

  updateKindleEmail: async (email: string, formKey: string): Promise<void> => {
    // Split email into username and domain (backend expects separate fields)
    const [username, domain] = email.split('@');

    const formData = new FormData();
    formData.append('kindle_email_address', username || '');
    formData.append('kindle_email_domain', domain || 'kindle.com');
    formData.append('form_key', formKey);
    // Add success_url to redirect back to React app after save
    formData.append('success_url', getSuccessUrl('/settings/kindle'));

    const response = await fetch('/user/kindle', {
      method: 'POST',
      body: formData,
      credentials: 'include',
      redirect: 'follow',
    });

    if (response.redirected && response.url) {
      const redirectUrl = new URL(response.url);
      if (redirectUrl.origin !== window.location.origin) {
        window.location.href = response.url;
        throw new Error('Redirected to external URL');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || errorData.message || 'Failed to update Kindle email',
      );
    }
  },

  sendKindleNow: async (formKey: string): Promise<void> => {
    const formData = new FormData();
    formData.append('form_key', formKey);
    // Add success_url to redirect back to React app after save
    formData.append('success_url', getSuccessUrl('/settings/kindle'));

    const response = await fetch('/user/kindle_send_now', {
      method: 'POST',
      body: formData,
      credentials: 'include',
      redirect: 'follow',
    });

    if (response.redirected && response.url) {
      const redirectUrl = new URL(response.url);
      if (redirectUrl.origin !== window.location.origin) {
        window.location.href = response.url;
        throw new Error('Redirected to external URL');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || errorData.message || 'Failed to send articles to Kindle',
      );
    }
  },

  // Data
  exportCsv: async (formKey: string): Promise<ActionStatusResult> => {
    const formData = new FormData();
    formData.append('form_key', formKey);

    // The X-Requested-With header makes the backend return a JSON status
    // (message + type) instead of a flash-cookie redirect, so we can surface
    // the result as a toast directly. Let the browser set the multipart
    // Content-Type/boundary — don't pass one here.
    const response = await fetch('/user/export/csv', {
      method: 'POST',
      headers: dataHeaders(),
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || 'Failed to export CSV');
    }

    try {
      return await response.json();
    } catch {
      return {};
    }
  },

  importData: async (
    file: File,
    service: string,
    formKey: string,
  ): Promise<ImportResult> => {
    const formData = new FormData();
    formData.append('form_key', formKey);

    // Map service to file field name
    const fileFieldMap: Record<string, string> = {
      pocket: 'pocket_file',
      matter: 'matter_file',
      readwise: 'readwise_file',
      raindrop: 'raindrop_file',
      pinboard: 'pinboard_file',
      instapaper: 'instapaper_file',
    };

    const fileField = fileFieldMap[service];
    if (!fileField) {
      throw new Error(`Unknown import service: ${service}`);
    }

    formData.append(fileField, file);

    // The X-Requested-With header makes the backend return a JSON status
    // (message + type) instead of a flash-cookie redirect, so we can surface
    // the result as a toast directly. Let the browser set the multipart
    // Content-Type/boundary — don't pass one here.
    const response = await fetch(`/user/import/${service}`, {
      method: 'POST',
      headers: dataHeaders(),
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || errorData.error || `Failed to import ${service} data`,
      );
    }

    // Try to parse response as JSON (some imports return ImportResult)
    try {
      return await response.json();
    } catch {
      // If not JSON, return a default success result
      return { imported: 0, failed: 0 };
    }
  },

  // Account
  deleteAccount: async (formKey: string): Promise<void> => {
    const formData = new FormData();
    formData.append('form_key', formKey);
    formData.append('confirm', '1');

    const response = await fetch('/user/delete', {
      method: 'POST',
      body: formData,
      credentials: 'include',
      redirect: 'follow',
    });

    if (response.redirected && response.url) {
      const redirectUrl = new URL(response.url);
      if (redirectUrl.origin !== window.location.origin) {
        window.location.href = response.url;
        throw new Error('Redirected to external URL');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || 'Failed to delete account');
    }

    // The backend cleared the auth cookies and 302-redirected, but fetch
    // follows redirects in the background without navigating the page, so we
    // must finish the logout client-side and hard-navigate to the logged-out
    // landing page ourselves.
    clearClientSession();
    window.location.href = '/';
  },

  // Premium/Stripe
  // Note: Stripe checkout and update redirect to external Stripe URLs
  // These should use form submission, not fetch
  // But we provide methods for unsubscribe and autorenew which redirect internally

  unsubscribeStripe: async (formKey: string): Promise<void> => {
    const formData = new FormData();
    formData.append('form_key', formKey);
    // Add success_url to redirect back to React app after save
    formData.append('success_url', getSuccessUrl('/settings/premium'));

    const response = await fetch('/user/stripe/unsubscribe', {
      method: 'POST',
      body: formData,
      credentials: 'include',
      redirect: 'follow',
    });

    if (response.redirected && response.url) {
      const redirectUrl = new URL(response.url);
      if (redirectUrl.origin !== window.location.origin) {
        window.location.href = response.url;
        throw new Error('Redirected to external URL');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || 'Failed to unsubscribe');
    }
  },

  enableStripeAutorenew: async (formKey: string): Promise<void> => {
    const formData = new FormData();
    formData.append('form_key', formKey);
    // Add success_url to redirect back to React app after save
    formData.append('success_url', getSuccessUrl('/settings/premium'));

    const response = await fetch('/user/stripe/autorenew', {
      method: 'POST',
      body: formData,
      credentials: 'include',
      redirect: 'follow',
    });

    if (response.redirected && response.url) {
      const redirectUrl = new URL(response.url);
      if (redirectUrl.origin !== window.location.origin) {
        window.location.href = response.url;
        throw new Error('Redirected to external URL');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || errorData.message || 'Failed to enable autorenew',
      );
    }
  },

  updateSharingPreferences: async (
    service: string,
    type: 'likes' | 'comments',
    value: boolean,
    formKey: string,
  ): Promise<void> => {
    const formData = new FormData();
    formData.append('form_key', formKey);

    // Map service and type to backend field names
    const fieldMap: Record<string, Record<'likes' | 'comments', string>> = {
      twitter: {
        likes: 'send_starred_to_twitter',
        comments: 'send_highlights_to_twitter',
      },
      tumblr: {
        likes: 'send_starred_to_tumblr',
        comments: 'send_highlights_to_tumblr',
      },
      pinboard: {
        likes: 'send_starred_to_pinboard',
        comments: 'send_highlights_to_pinboard',
      },
      evernote: {
        likes: 'send_starred_to_evernote',
        comments: 'send_highlights_to_evernote',
      },
      notion: {
        likes: 'send_starred_to_notion',
        comments: 'send_highlights_to_notion',
      },
    };

    const fieldName = fieldMap[service]?.[type];
    if (!fieldName) {
      throw new Error(`Invalid service or type: ${service}/${type}`);
    }

    // Backend expects user[field_name] format
    formData.append(`user[${fieldName}]`, value ? '1' : '0');

    const response = await fetch('/user', {
      method: 'POST',
      body: formData,
      credentials: 'include',
      redirect: 'follow',
    });

    if (response.redirected && response.url) {
      const redirectUrl = new URL(response.url);
      if (redirectUrl.origin !== window.location.origin) {
        window.location.href = response.url;
        throw new Error('Redirected to external URL');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || errorData.message || 'Failed to update sharing preferences',
      );
    }
  },
};
